/**
 * POS-I188 — display names of plans and add-ons through the catalog translation
 * layer (locales/<lang>/catalog.json, namespace `catalog`). The stored English
 * name stays the code/fallback: a plan or add-on that has no translation
 * (a custom add-on created in the platform console) shows its stored name.
 */
import i18n from 'i18next';
import catalogEn from '@/locales/en/catalog.json';

const EN_ADDON_KEY_BY_NAME = new Map<string, string>(
  Object.entries(catalogEn.addons).map(([key, name]) => [name, key]),
);

export function addonLabel(key: string | null | undefined, fallbackName: string): string {
  const k = key ?? EN_ADDON_KEY_BY_NAME.get(fallbackName);
  if (!k) return fallbackName;
  return i18n.t(`addons.${k}`, { ns: 'catalog', defaultValue: fallbackName }) as string;
}

export function planLabel(name: string | null | undefined): string {
  if (!name) return '';
  return i18n.t(`plans.${name}`, { ns: 'catalog', defaultValue: name }) as string;
}

/**
 * Translate one stored bill line ("<Plan> Plan (base)", "<Add-on>",
 * "<Add-on> x3 (3 @ PKR 200)"). Anything unrecognised is returned unchanged.
 */
export function billLineLabel(description: string): string {
  const base = /^(.*) Plan \(base\)$/.exec(description);
  if (base) return i18n.t('planBase', { ns: 'catalog', plan: planLabel(base[1]), defaultValue: description }) as string;
  const direct = EN_ADDON_KEY_BY_NAME.get(description);
  if (direct) return addonLabel(direct, description);
  const qty = /^(.*?) x(\d+) \((\d+) @ PKR ([\d,.]+)\)$/.exec(description);
  if (qty) {
    const key = EN_ADDON_KEY_BY_NAME.get(qty[1]);
    if (key) return `${addonLabel(key, qty[1])} ×${qty[2]} (${qty[3]} @ PKR ${qty[4]})`;
  }
  return description;
}
